/**
 * Mind Over City — 30 handcrafted-feeling parameter-driven survival levels.
 *
 * REVISED (2026-02-10): Time-based survival, not collection-based.
 *   • Every level has a countdown timer (duration in seconds).
 *   • Player advances by surviving the timer. That's it.
 *   • Focus Orbs remain — collecting them is optional and rewards stars,
 *     Focus Points, and achievement progress.
 *
 * Star rules (uniform):
 *   ⭐   Survive the timer.
 *   ⭐⭐  Survive + collect ≥ 50% of the level's orb ceiling.
 *   ⭐⭐⭐ Survive + collect ≥ 90% orbs + zero close-calls.
 */

export const WORLDS = [
  { id: 1, name: 'Learn',   range: [1, 10],  color: '#0B192C', accent: '#C5A059', tagline: 'Basics. Wide gates. Calm air.' },
  { id: 2, name: 'Focus',   range: [11, 20], color: '#0F1A2E', accent: '#8FB8FF', tagline: 'Wind, distractions, narrower gates.' },
  { id: 3, name: 'Mastery', range: [21, 30], color: '#0B1E29', accent: '#7CE7C1', tagline: 'Everything at once. Precision.'      },
];

/** Return the world containing this level (1-30). */
export function worldForLevel(level) {
  return WORLDS.find(w => level >= w.range[0] && level <= w.range[1]) || WORLDS[0];
}

/**
 * Level factory — parameter-driven with per-world flavour + gentle progression.
 * Everything is deterministic so the same level always plays the same on any device.
 */
function buildLevel(n) {
  const w = worldForLevel(n);
  const inWorldT = (n - w.range[0]) / (w.range[1] - w.range[0]);
  const overallT = (n - 1) / 29;
  const lerp = (a, b, t) => a + (b - a) * t;

  // Course length (Focus Orb gates to pass to clear the level).
  //   Learn  (L1-10):  6 → 12 gates    (~20-35 s courses)
  //   Focus  (L11-20): 14 → 22 gates   (~35-55 s courses)
  //   Mastery(L21-30): 24 → 40 gates   (~55-90 s courses)
  const courseGates = w.id === 1
    ? Math.round(lerp(6, 12, inWorldT))
    : w.id === 2
      ? Math.round(lerp(14, 22, inWorldT))
      : Math.round(lerp(24, 40, inWorldT));

  const gapMul     = lerp(1.30, 0.82, overallT);
  const spawnEvery = lerp(2.9, 2.0, overallT);
  const spawnMin   = lerp(2.1, 1.15, overallT);
  const speed      = Math.round(lerp(58, 92, overallT));
  const speedRampMax = Math.round(lerp(24, 88, overallT));

  const weather = w.id === 1
    ? (inWorldT < 0.35 ? 'clear'  : inWorldT < 0.7 ? 'dusk'   : 'storm')
    : w.id === 2
      ? (inWorldT < 0.4  ? 'neon'   : inWorldT < 0.8 ? 'rain'   : 'aurora')
      : (inWorldT < 0.5  ? 'snow'   : inWorldT < 0.85 ? 'aurora' : 'aurora');

  const wind = w.id === 1 ? 0 : w.id === 2 ? (inWorldT > 0.4 ? 8 : 0) : (10 + inWorldT * 10);

  const variants = ['standard'];
  if (n >= 4)  variants.push('wide-thin');
  if (n >= 8)  variants.push('narrow');
  if (n >= 12) variants.push('moving');
  if (n >= 18) variants.push('double');

  const NAMES = [
    'First Breath', 'Steady Hand', 'Traffic Hum', 'Quiet Corners',
    'Rush Hour', 'Rooftop Air', 'Bridge Light', 'Alley Focus',
    'Downtown Weave', 'Skyline Clear',
    'Neon Threshold', 'Rain-Slick', 'Signal Static', 'Skyway Draft',
    'Chrome Corridor', 'Half-Second', 'The Whistle', 'Split-Second',
    'Between Buildings', 'The Radio',
    'First Light Above', 'Thin Air', 'The Ledge', 'Wind Ridge',
    'Cold Line', 'Aurora Curve', 'Snow Whistle', 'The Summit Draft',
    'Above the Storm', 'Master of Focus',
  ];
  const INTROS = [
    "Ease in. Don't chase — just let it come to you.",
    'Feel the rhythm before you push it. Match the gate, not the clock.',
    'Traffic hums. Your breath is louder.',
    'Fewer distractions. Read the space between them.',
    "Speed picks up. You don't. You match it.",
    'Higher air, cleaner line. Trust the drift.',
    "Bridge lights come and go. You're the constant.",
    'Tight corners. Small breaths.',
    'The city weaves you in. Weave back.',
    'Clear line to the horizon. This is your rhythm now.',
    'New sky. Same steady hand.',
    "Rain doesn't rush you. Neither should anything else.",
    'Static in the signal. Find the tone under it.',
    "A crosswind. Lean, don't fight.",
    'Long corridor. Let momentum work with you.',
    'Half a second either way. Feel the middle.',
    "A whistle in the wind. Doesn't mean stop.",
    "Precision under speed. That's the whole thing.",
    'Buildings close. Space still opens.',
    'The radio steadies down. So do you.',
    'Air thins. Focus sharpens.',
    "Every breath counts up here. Don't waste one.",
    "The ledge doesn't move. You do.",
    "Ridge wind. Read it, don't argue with it.",
    'Cold on your face. Warm in your chest.',
    'Follow the aurora curve. It knows where it goes.',
    "Snow whistle. It's beautiful. You're still here.",
    'Almost the top. Same discipline as the first step.',
    'The storm is below you now.',
    'Master run. This one is a gift to yourself.',
  ];

  return {
    id: n,
    world: w.id,
    name: NAMES[n - 1] || `Level ${n}`,
    intro: INTROS[n - 1] || 'Steady.',
    courseGates,                       // gates to pass = level completion
    // 6 distinct city themes rotate across the 30 levels
    skylineTheme:
      n <= 5  ? 'grid'      :          // L1-5   · Downtown Grid
      n <= 10 ? 'bridge'    :          // L6-10  · Bridge & Docks
      n <= 15 ? 'neon'      :          // L11-15 · Neon District
      n <= 20 ? 'overpass'  :          // L16-20 · Rainy Overpass
      n <= 25 ? 'rooftops'  :          // L21-25 · Skyline Rooftops
                'ridge',                // L26-30 · Mountain Ridge
    starThresholds: {
      one:   0,                        // ⭐  = complete course (any close-calls)
      two:   1,                        // ⭐⭐ = complete with ≤ 1 close-call
      three: 0,                        // ⭐⭐⭐ = complete with 0 close-calls
    },
    gapMul, spawnEvery, spawnMin, speed, speedRampMax,
    variants, wind, weather,
  };
}

export const LEVELS = Array.from({ length: 30 }, (_, i) => buildLevel(i + 1));
export const MAX_LEVEL = 30;

/**
 * Award stars based on a completed course run.
 * @param {object} level  the level config
 * @param {object} run    { completed:bool, nearMisses:number }
 * @returns 0 | 1 | 2 | 3
 */
export function computeStars(level, run) {
  if (!run.completed) return 0;             // Course must be completed
  const near = run.nearMisses ?? 0;
  if (near === 0) return 3;                 // ⭐⭐⭐ flawless
  if (near <= 1) return 2;                  // ⭐⭐ near-perfect
  return 1;                                  // ⭐ completed
}

/** Endless Flow Mode unlocks after clearing level 15 (halfway through World 2). */
export const ENDLESS_UNLOCK_LEVEL = 15;
/** Master Flow Mode unlocks after clearing the last level of the campaign. */
export const MASTER_UNLOCK_LEVEL  = 30;
